import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Plus } from 'lucide-react';
import { cn } from '../../lib/utils';

const STORAGE_KEY = 'kubair.workspace.pitch';
const MAX_TABS = 8;

/**
 * PitchReportTabBar — a lightweight, persistent tab strip for pitch reports.
 *
 * Each tab is a symbol (NSE ticker). Selecting a tab navigates to that
 * symbol's pitch report page. The current `activeSymbol` is added to the tab
 * list on mount (deduped) so a user's recently-viewed set is always there.
 *
 * Props:
 *   activeSymbol  — string. The symbol currently on screen. Will be added/
 *                   activated in the tab list.
 *   className     — optional extra classes.
 */
export default function PitchReportTabBar({ activeSymbol, className }) {
  const navigate = useNavigate();
  const [tabs, setTabs] = React.useState(() => loadTabs());

  // Whenever activeSymbol changes (route change), add it to the tab list.
  useEffect(() => {
    if (!activeSymbol) return;
    setTabs((current) => {
      if (current.find((t) => t.symbol === activeSymbol)) return current;
      const next = [...current, { symbol: activeSymbol }];
      // cap at MAX_TABS — drop the oldest non-active tab if needed.
      while (next.length > MAX_TABS) {
        const idx = next.findIndex((t) => t.symbol !== activeSymbol);
        next.splice(idx >= 0 ? idx : 0, 1);
      }
      return next;
    });
  }, [activeSymbol]);

  // Persist.
  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tabs)); }
    catch (e) { /* swallow */ }
  }, [tabs]);

  const close = (symbol, ev) => {
    if (ev) { ev.stopPropagation(); ev.preventDefault(); }
    setTabs((current) => {
      const next = current.filter((t) => t.symbol !== symbol);
      if (symbol === activeSymbol) {
        const fallback = next[next.length - 1];
        if (fallback) navigate(`/public/pitch-report/${fallback.symbol}`);
        else navigate('/public/research');
      }
      return next;
    });
  };

  const open = () => {
    const next = window.prompt('Open pitch report for NSE symbol:', '');
    if (!next) return;
    navigate(`/public/pitch-report/${next.trim().toUpperCase()}`);
  };

  const displayed = useMemo(() => {
    if (!activeSymbol || tabs.find((t) => t.symbol === activeSymbol)) return tabs;
    return [...tabs, { symbol: activeSymbol }];
  }, [tabs, activeSymbol]);

  if (displayed.length === 0) return null;

  return (
    <div className={cn(
      'flex items-stretch overflow-x-auto border-b border-border bg-muted/30 text-xs',
      className,
    )} role="tablist" aria-label="Pitch report workspace">
      {displayed.map((t) => {
        const isActive = t.symbol === activeSymbol;
        return (
          <button
            key={t.symbol}
            role="tab"
            aria-selected={isActive}
            onClick={() => isActive ? null : navigate(`/public/pitch-report/${t.symbol}`)}
            className={cn(
              'group flex items-center gap-1.5 border-r border-border px-3 py-2 select-none',
              isActive
                ? 'bg-background text-foreground border-b-2 border-b-primary -mb-px'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
            )}
            title={`Pitch report: ${t.symbol}`}
          >
            <span className="font-medium">{t.symbol}</span>
            <span
              role="button"
              tabIndex={0}
              onClick={(ev) => close(t.symbol, ev)}
              onKeyDown={(ev) => { if (ev.key === 'Enter') close(t.symbol, ev); }}
              className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded text-muted-foreground/70 opacity-0 group-hover:opacity-100 hover:bg-muted hover:text-foreground"
              aria-label={`Close ${t.symbol}`}
            >
              <X className="h-3 w-3" />
            </span>
          </button>
        );
      })}
      <button
        type="button"
        onClick={open}
        className="flex items-center px-2 text-muted-foreground hover:text-foreground hover:bg-muted/50"
        title="Open another symbol"
        aria-label="New tab"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}

function loadTabs() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.filter((t) => t && typeof t.symbol === 'string').slice(0, MAX_TABS);
  } catch (e) { return []; }
}
